import type { ReactNode } from "react";

import type { PVSizingResponse } from "../services/pvSizing";
import type { PVSizingWarningCode } from "../services/pvSizingContract";
import { EMPTY_QUANTITY, formatQuantity } from "../utils/formatQuantity";
import { SCENARIO_LABELS } from "./LoadRow";
import { REDUNDANCY_LABELS, statusHeading } from "./PVResultSummary";
import {
  PV_BATTERY_CONFIGURATION_LABELS,
  PV_DG_COEXISTENCE_LABELS,
  PV_PHASE_CONFIGURATION_LABELS,
  PV_SYSTEM_TYPE_LABELS,
} from "./pvStudyDraft";

interface ValueRow {
  key: string;
  label: string;
  // Decimal values are exact backend strings: four significant figures on
  // screen, the exact value in the tooltip (display rule A12). The unit is in
  // the row label. A count is a number and is shown as the integer it is, with
  // no tooltip. null renders as an em dash.
  value: string | number | null;
  // The warnings that say this figure was assumed rather than established. A
  // single input names its own code; the combined derating factor names both,
  // because either one makes the product an assumption too.
  notEstablishedCodes?: readonly PVSizingWarningCode[];
  // What follows the figure when one of those warnings is present.
  assumedSuffix?: string;
  // A percentage keeps its sign with the figure, as the UPS loading rows do.
  unit?: string;
  // A limit row names the check row it sits beside, so the screening reads as
  // a figure and the limit it was held against.
  limitFor?: string;
}

interface PVResultPanelProps {
  result: PVSizingResponse;
}

const NOT_ESTABLISHED_SUFFIX = " (not established)";
const INCLUDES_NOT_ESTABLISHED_SUFFIX = " (includes a not-established factor)";

/**
 * The loading figure is not the running load over the nameplate: it is the
 * inverter AC requirement over the derated installed duty capacity. The label
 * says so in full, as the UPS loading labels do.
 */
export const AC_LOADING_LABEL =
  "AC loading (inverter AC requirement kW ÷ derated installed duty capacity kW)";

/** Carried by every result (A19 (b)): what the array figures do not cover. */
export const YIELD_NOTE =
  "Annual energy yield, irradiance, shading, soiling and system losses are not evaluated " +
  "(GAP-019); the array is sized from the inverter AC requirement and the target DC/AC " +
  "ratio only.";

// The two factors the combined derating factor is the product of.
const DERATING_CODES = [
  "AMBIENT_DERATING_NOT_ESTABLISHED",
  "ALTITUDE_DERATING_NOT_ESTABLISHED",
] as const satisfies readonly PVSizingWarningCode[];

// The calculation ladder, in the engine's order: required AC output, growth and
// margin to the design AC output, the AC efficiency to the inverter AC
// requirement, and derating to the capacity the schedule must cover.
function acRows(result: PVSizingResponse): ValueRow[] {
  return [
    {
      key: "required_ac_output_kw",
      label: "Required AC output (kW)",
      value: result.required_ac_output_kw,
    },
    {
      key: "ac_efficiency_factor",
      label: "AC efficiency factor",
      value: result.ac_efficiency_factor,
      notEstablishedCodes: ["AC_EFFICIENCY_NOT_ESTABLISHED"],
    },
    {
      key: "future_growth_factor",
      label: "Future growth factor",
      value: result.future_growth_factor,
      notEstablishedCodes: ["GROWTH_FACTOR_NOT_ESTABLISHED"],
    },
    {
      key: "design_margin_factor",
      label: "Design margin factor",
      value: result.design_margin_factor,
      notEstablishedCodes: ["DESIGN_MARGIN_NOT_ESTABLISHED"],
    },
    {
      key: "design_ac_output_kw",
      label: "Design AC output, required AC output × growth × margin (kW)",
      value: result.design_ac_output_kw,
    },
    {
      key: "inverter_ac_requirement_kw",
      label: "Inverter AC requirement, design AC output ÷ AC efficiency (kW)",
      value: result.inverter_ac_requirement_kw,
    },
    {
      key: "ambient_derating_factor",
      label: "Ambient derating factor",
      value: result.ambient_derating_factor,
      notEstablishedCodes: ["AMBIENT_DERATING_NOT_ESTABLISHED"],
    },
    {
      key: "altitude_derating_factor",
      label: "Altitude derating factor",
      value: result.altitude_derating_factor,
      notEstablishedCodes: ["ALTITUDE_DERATING_NOT_ESTABLISHED"],
    },
    {
      key: "combined_derating_factor",
      label: "Combined derating factor",
      value: result.combined_derating_factor,
      notEstablishedCodes: DERATING_CODES,
      assumedSuffix: INCLUDES_NOT_ESTABLISHED_SUFFIX,
    },
    {
      key: "derated_inverter_requirement_kw",
      label: "Derated inverter requirement (kW)",
      value: result.derated_inverter_requirement_kw,
    },
  ];
}

// The module data and site temperatures, the corrected module voltages, the
// string-length limits each beside the inverter voltages that drive them, the
// array, and then each check of the string screening beside its limit (A19 (e),
// (f)). Every figure is the engine's; nothing is compared or recomputed here.
function arrayRows(result: PVSizingResponse): ValueRow[] {
  return [
    {
      key: "module_rated_power_wp",
      label: "Module rated power (Wp)",
      value: result.module_rated_power_wp,
    },
    {
      key: "module_open_circuit_voltage_v",
      label: "Module open-circuit voltage, Voc (V)",
      value: result.module_open_circuit_voltage_v,
    },
    {
      key: "module_maximum_power_voltage_v",
      label: "Module maximum-power voltage, Vmp (V)",
      value: result.module_maximum_power_voltage_v,
    },
    {
      key: "module_short_circuit_current_a",
      label: "Module short-circuit current, Isc (A)",
      value: result.module_short_circuit_current_a,
    },
    {
      key: "temperature_coefficient_voc_percent_per_c",
      label: "Temperature coefficient of Voc (%/°C)",
      value: result.temperature_coefficient_voc_percent_per_c,
    },
    {
      key: "temperature_coefficient_vmp_percent_per_c",
      label: "Temperature coefficient of Vmp (%/°C)",
      value: result.temperature_coefficient_vmp_percent_per_c,
    },
    {
      key: "minimum_design_temperature_c",
      label: "Minimum design temperature (°C)",
      value: result.minimum_design_temperature_c,
    },
    {
      key: "maximum_cell_temperature_c",
      label: "Maximum cell temperature (°C)",
      value: result.maximum_cell_temperature_c,
    },
    {
      key: "cold_corrected_module_voc_v",
      label: "Module Voc at the minimum design temperature (V)",
      value: result.cold_corrected_module_voc_v,
    },
    {
      key: "cold_corrected_module_vmp_v",
      label: "Module Vmp at the minimum design temperature (V)",
      value: result.cold_corrected_module_vmp_v,
    },
    {
      key: "hot_corrected_module_vmp_v",
      label: "Module Vmp at the maximum cell temperature (V)",
      value: result.hot_corrected_module_vmp_v,
    },
    {
      key: "maximum_modules_per_string",
      label: "Maximum modules per string, n_max",
      value: result.maximum_modules_per_string,
    },
    {
      key: "inverter_mppt_max_voltage_v",
      label: "MPPT maximum voltage (V)",
      value: result.inverter_mppt_max_voltage_v,
      limitFor: "maximum_modules_per_string",
    },
    {
      key: "inverter_max_dc_voltage_v",
      label: "Inverter maximum DC voltage (V)",
      value: result.inverter_max_dc_voltage_v,
      limitFor: "maximum_modules_per_string",
    },
    {
      key: "minimum_modules_per_string",
      label: "Minimum modules per string, n_min",
      value: result.minimum_modules_per_string,
    },
    {
      key: "inverter_mppt_min_voltage_v",
      label: "MPPT minimum voltage (V)",
      value: result.inverter_mppt_min_voltage_v,
      limitFor: "minimum_modules_per_string",
    },
    {
      key: "modules_per_string",
      label: "Modules per string",
      value: result.modules_per_string,
    },
    {
      key: "required_dc_array_capacity_kwp",
      label: "Required DC array capacity, inverter AC requirement × target DC/AC ratio (kWp)",
      value: result.required_dc_array_capacity_kwp,
    },
    { key: "modules_required", label: "Modules required", value: result.modules_required },
    { key: "total_strings", label: "Strings", value: result.total_strings },
    { key: "installed_modules", label: "Installed modules", value: result.installed_modules },
    {
      key: "installed_dc_array_capacity_kwp",
      label: "Installed DC array capacity (kWp)",
      value: result.installed_dc_array_capacity_kwp,
    },
    {
      key: "cold_string_voc_v",
      label: "Cold string Voc (V)",
      value: result.cold_string_voc_v,
    },
    {
      // The same limit as above, repeated so the cold string reads against it.
      key: "cold_string_voc_limit_v",
      label: "Inverter maximum DC voltage (V)",
      value: result.inverter_max_dc_voltage_v,
      limitFor: "cold_string_voc_v",
    },
    { key: "strings_per_mppt", label: "Strings per MPPT", value: result.strings_per_mppt },
    {
      key: "maximum_strings_per_mppt",
      label: "Maximum strings per MPPT",
      value: result.maximum_strings_per_mppt,
      limitFor: "strings_per_mppt",
    },
    {
      key: "mppt_input_current_a",
      label: "MPPT input current, strings per MPPT × module Isc (A)",
      value: result.mppt_input_current_a,
    },
    {
      key: "inverter_max_input_current_per_mppt_a",
      label: "Maximum input current per MPPT (A)",
      value: result.inverter_max_input_current_per_mppt_a,
      limitFor: "mppt_input_current_a",
    },
    { key: "mppt_count", label: "MPPT inputs per inverter", value: result.mppt_count },
  ];
}

function capacityRows(result: PVSizingResponse): ValueRow[] {
  return [
    {
      key: "installed_duty_capacity_kw",
      label: "Installed duty AC capacity (kW)",
      value: result.installed_duty_capacity_kw,
    },
    {
      key: "total_installed_capacity_kw",
      label: "Total installed AC capacity (kW)",
      value: result.total_installed_capacity_kw,
    },
    {
      key: "derated_duty_capacity_kw",
      label: "Derated installed duty capacity (kW)",
      value: result.derated_duty_capacity_kw,
    },
    {
      key: "spare_derated_capacity_kw",
      label: "Spare derated capacity (kW)",
      value: result.spare_derated_capacity_kw,
    },
    // The long label leaves no room for a unit, so the percent sign stays with the figure.
    {
      key: "ac_loading_percent",
      label: AC_LOADING_LABEL,
      value: result.ac_loading_percent,
      unit: "%",
    },
    {
      key: "target_dc_ac_ratio",
      label: "Target DC/AC ratio",
      value: result.target_dc_ac_ratio,
    },
    {
      key: "installed_dc_ac_ratio",
      label: "Installed DC/AC ratio (installed kWp ÷ installed duty AC capacity kW)",
      value: result.installed_dc_ac_ratio,
    },
  ];
}

function ValueRows({
  rows,
  warningCodes,
}: {
  rows: ValueRow[];
  warningCodes: ReadonlySet<string>;
}) {
  return (
    <>
      {rows.map((row) => {
        if (typeof row.value === "number") {
          return (
            <tr key={row.key} data-row-key={row.key} data-limit-for={row.limitFor}>
              <th scope="row">{row.label}</th>
              {/* Counts, not engineering quantities. */}
              <td>{row.value}</td>
            </tr>
          );
        }
        const quantity = formatQuantity(row.value, row.unit);
        // An input is always a figure; only the warning says whether the
        // engineer established it (A16 (a), A19 (h)). The tooltip keeps the
        // exact value either way.
        const assumed = row.notEstablishedCodes?.some((code) => warningCodes.has(code)) ?? false;
        const suffix = row.assumedSuffix ?? NOT_ESTABLISHED_SUFFIX;
        return (
          <tr key={row.key} data-row-key={row.key} data-limit-for={row.limitFor}>
            <th scope="row">{row.label}</th>
            <td title={quantity.exact ?? undefined}>
              {assumed ? `${quantity.display}${suffix}` : quantity.display}
            </td>
          </tr>
        );
      })}
    </>
  );
}

function ValueTable({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section aria-label={title}>
      <h3>{title}</h3>
      <div data-table-scroll>
        <table>
          <thead>
            <tr>
              <th scope="col">Parameter</th>
              <th scope="col">Value</th>
            </tr>
          </thead>
          <tbody>{children}</tbody>
        </table>
      </div>
    </section>
  );
}

export function PVResultPanel({ result }: PVResultPanelProps) {
  const warningCodes = new Set<string>(result.warnings.map((warning) => warning.code));
  const dgAnswer = result.dg_coexistence === null ? "" : result.dg_coexistence ? "yes" : "no";

  return (
    <article aria-label="Solar PV sizing result">
      <header>
        <h2>Solar PV sizing result</h2>
        <p>
          Study code: <strong data-study-code="true">{result.code}</strong>
          {" - "}
          {result.name}
        </p>
        <p>
          Overall status:{" "}
          <strong data-result-status={result.status}>{statusHeading(result)}</strong>
        </p>
      </header>

      <ValueTable title="AC requirement and design">
        <ValueRows rows={acRows(result)} warningCodes={warningCodes} />
      </ValueTable>

      <ValueTable title="PV array and strings">
        <ValueRows rows={arrayRows(result)} warningCodes={warningCodes} />
      </ValueTable>
      <p data-yield-note="true">{YIELD_NOTE}</p>

      <ValueTable title="Inverter selection">
        <tr data-row-key="required_unit_rating_kw">
          <th scope="row">Required inverter rating, after derating (kW)</th>
          <td title={formatQuantity(result.required_unit_rating_kw).exact ?? undefined}>
            {formatQuantity(result.required_unit_rating_kw).display}
          </td>
        </tr>
        <tr data-row-key="selected_unit_rating_kw">
          <th scope="row">Selected inverter rating (kW)</th>
          {/* A catalogue value from the project design basis: shown as
              sent, so the engineer reads back an orderable rating. */}
          <td>{result.selected_unit_rating_kw ?? EMPTY_QUANTITY}</td>
        </tr>
        <tr data-row-key="available_inverter_ratings_kw">
          <th scope="row">Rating schedule considered (kW)</th>
          {/* The schedule the engine chose from, exactly as it was sent: the
              evidence shows which ratings were on the table. */}
          <td>{result.available_inverter_ratings_kw.join(" / ")}</td>
        </tr>
        <tr data-row-key="duty_inverters">
          <th scope="row">Duty inverters</th>
          {/* Counts, not engineering quantities. */}
          <td>{result.duty_inverters}</td>
        </tr>
        <tr data-row-key="redundant_inverters">
          <th scope="row">Redundant inverters</th>
          <td>{result.redundant_inverters}</td>
        </tr>
        <tr data-row-key="total_inverters">
          <th scope="row">Total inverters</th>
          <td>{result.total_inverters}</td>
        </tr>
        <tr data-row-key="redundancy_mode">
          <th scope="row">Redundancy mode</th>
          <td data-redundancy-mode={result.redundancy_mode}>
            {REDUNDANCY_LABELS[result.redundancy_mode]}
          </td>
        </tr>
        <ValueRows rows={capacityRows(result)} warningCodes={warningCodes} />
      </ValueTable>

      <ValueTable title="Grid and DG">
        {/* Recorded with the study and echoed only (A19 (k)). */}
        <ValueRows
          rows={[
            { key: "export_limit_kw", label: "Export limit (kW)", value: result.export_limit_kw },
          ]}
          warningCodes={warningCodes}
        />
        <tr data-row-key="dg_coexistence">
          <th scope="row">Operates alongside a DG</th>
          <td data-dg-coexistence={dgAnswer || undefined}>
            {PV_DG_COEXISTENCE_LABELS[dgAnswer]}
          </td>
        </tr>
      </ValueTable>

      <ValueTable title="Descriptors">
        {/* Recorded with the study; none of them changes a calculated value (A19 (j)). */}
        <tr data-row-key="system_type">
          <th scope="row">System type</th>
          <td data-system-type={result.system_type ?? undefined}>
            {result.system_type === null
              ? EMPTY_QUANTITY
              : PV_SYSTEM_TYPE_LABELS[result.system_type]}
          </td>
        </tr>
        <tr data-row-key="phase_configuration">
          <th scope="row">Phase configuration</th>
          <td data-phase-configuration={result.phase_configuration ?? undefined}>
            {result.phase_configuration === null
              ? EMPTY_QUANTITY
              : PV_PHASE_CONFIGURATION_LABELS[result.phase_configuration]}
          </td>
        </tr>
        <tr data-row-key="battery_configuration">
          <th scope="row">Battery configuration</th>
          <td data-battery-configuration={result.battery_configuration ?? undefined}>
            {result.battery_configuration === null
              ? EMPTY_QUANTITY
              : PV_BATTERY_CONFIGURATION_LABELS[result.battery_configuration]}
          </td>
        </tr>
        <tr data-row-key="scenario">
          <th scope="row">Scenario</th>
          <td data-scenario={result.scenario ?? undefined}>
            {result.scenario === null ? EMPTY_QUANTITY : SCENARIO_LABELS[result.scenario]}
          </td>
        </tr>
      </ValueTable>

      <section aria-label="Jurisdiction profile">
        <h3>Jurisdiction profile</h3>
        <p data-jurisdiction-profile={result.jurisdiction_profile}>
          Designed under the {result.jurisdiction_profile} profile. The sizing itself carries
          no profile-dependent value; the profile is recorded with the run and must match the
          design basis of the project revision.
        </p>
      </section>

      <footer>
        <p>
          Calculation results are engineering design checks, not a statutory compliance
          declaration. Confirm the governing project basis, the required AC output, the AC
          efficiency factor, the module and inverter datasheet values, the site temperatures,
          the target DC/AC ratio, the rating schedule and every growth, margin and derating
          factor before approval or issue. Annual energy yield is not evaluated (GAP-019).
        </p>
      </footer>
    </article>
  );
}

export default PVResultPanel;
